"use client";

import { Clock3 } from "lucide-react";
import {
  HERO_ALT,
  HERO_HEIGHT,
  HERO_SIZES,
  HERO_SRC,
  HERO_SRCSET,
  HERO_WIDTH,
} from "@/lib/hero-media";
import { Cta } from "./cta";
import { useQuote } from "./quote-context";

export function Hero() {
  const { goToQuote } = useQuote();

  const scrollTo = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="top"
      className="relative -mt-[72px] flex min-h-[100svh] items-end overflow-hidden bg-ink pt-[72px]"
    >
      <div className="absolute inset-0">
        <img
          src={HERO_SRC}
          srcSet={HERO_SRCSET}
          sizes={HERO_SIZES}
          alt={HERO_ALT}
          width={HERO_WIDTH}
          height={HERO_HEIGHT}
          fetchPriority="high"
          decoding="sync"
          loading="eager"
          className="size-full scale-105 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/30 to-transparent" />
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-5 pb-20 pt-28 sm:pb-28">
        <p className="eyebrow hero-rise text-brass" style={{ animationDelay: "0.1s" }}>
          Family-owned • Stockton, Manitoba • Since 2019
        </p>

        <h1 className="mt-6 max-w-[16ch] font-display text-[clamp(2.6rem,7.5vw,6.5rem)] leading-[0.92] font-bold text-softwhite">
          <EngraveLine text="Turn the right product" delay={0.25} />
          <EngraveLine text="into something" delay={0.45} />
          <EngraveLine text="unforgettable." delay={0.65} accent />
        </h1>

        <p
          className="hero-rise mt-8 max-w-xl text-base leading-relaxed text-softwhite/75 sm:text-lg"
          style={{ animationDelay: "1s" }}
        >
          Custom-engraved promotional products, business gifts, and personalized
          pieces—guided by real people from the first idea to the finished result.
        </p>

        <div className="hero-rise mt-10 flex flex-wrap items-center gap-3" style={{ animationDelay: "1.15s" }}>
          <Cta onClick={() => goToQuote()}>Request a Custom Quote</Cta>
          <Cta
            variant="ghostLight"
            withArrow={false}
            onClick={() => scrollTo("categories")}
          >
            Explore Product Ideas
          </Cta>
        </div>

        <p className="hero-rise mt-5 text-xs text-softwhite/55" style={{ animationDelay: "1.35s" }}>
          Already have a logo or idea? Include it with your quote request.
        </p>

        <div
          className="hero-rise mt-12 inline-flex items-center gap-3 rounded-full border border-brass/30 bg-ink/50 px-5 py-3 backdrop-blur-md"
          style={{ animationDelay: "1.5s" }}
        >
          <span className="relative flex size-2">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-brass opacity-60" />
            <span className="relative inline-flex size-2 rounded-full bg-brass" />
          </span>
          <Clock3 className="size-4 text-brass" aria-hidden />
          <span className="text-xs font-medium text-softwhite/85">
            Friendly, personal responses within one business hour.
          </span>
        </div>
      </div>
    </section>
  );
}

function EngraveLine({
  text,
  delay,
  accent,
}: {
  text: string;
  delay: number;
  accent?: boolean;
}) {
  return (
    <span className="relative block overflow-hidden py-[0.04em]">
      <span
        className={`mask-line ${accent ? "engraved-text" : ""}`}
        style={{ animationDelay: `${delay + 0.28}s` }}
      >
        {text}
      </span>
      <span
        aria-hidden
        className="hero-line absolute bottom-[0.12em] left-0 h-[2px] w-full bg-brass"
        style={{ animationDelay: `${delay}s` }}
      />
    </span>
  );
}
